import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import SocketService, {
  socket,
} from "../../../../service/socket/SocketService";
import SocketChatsKeys from "../../../../const/SocketChatsKeys";
import { ConversationList } from "../../../../model/chats/ChatListModel";
import SessionStorage from "../../../../service/storage/SessionStorage";
import StorageKeys from "../../../../const/StorageKeys";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import {
  TeamMessage,
  TeamConversationMessageModel,
} from "../../../../model/chats/TeamConversationMessageModel";
import CacheStorage from "../../../../service/storage/CacheStorage";
import EncryptDecryptService, {
  GroupMessageEncryptedType,
  HybridEncrypted,
} from "../../../../service/encrypt_decrypt/EncryptDecryptService";
import TeamChatsProvider from "./TeamChatsProvider";
import {
  TeamMutedConversations,
  ParticipantDetails,
  TeamConversationDetailsModel,
  TeamResults,
} from "../../../../model/chats/TeamConversationDetailsModel";
import {
  ChatTextType,
  MuteSettingsType,
  UpdateBlockStatusParams,
  UpdateConversationSettingsParams,
} from "../../../../libraries/types_interface/chats/ChatsTypes";
import { toLocalDate } from "../../../../service/date_service/date_service";
import ChatsProvider from "../chats/ChatsProvider";
import { useSelector } from "react-redux";
import { RootState } from "../../../../libraries/state_reducers/store";
import ModalKeys from "../../../../const/ModalKeys";
import { useDispatch } from "react-redux";
import {
  addModal,
  removeModal,
} from "../../../../libraries/state_reducers/widget/ModalsReducer";
import {
  chatRefreshListStatus,
  mediaDeleteChooseAction,
  messageDeleteChooseAction,
  sendChatMessage,
  sendForwardMessages,
  setForwardMediaList,
} from "../../../../libraries/state_reducers/chats/ChatsReducer";
import { ResponsiveDeviceType } from "../../../../widget/layouts/ScreenMedia";
import ImagePickerPreviewProvider from "../../../widget/ImagePickerPreviewProvider";
import { UploadEncryptedMedia } from "../../../../model/chats/ChatMediaUploadedDetailModel";
import {
  audioExtensions,
  docExtensions,
  imageExtensions,
  pdfExtensions,
  videoExtensions,
} from "../../../../const/FileExtensions";
import Loader from "../../../../widget/common/ui/Loader";
import { isMobile } from "react-device-detect";
import ShowToast from "../../../../widget/common/ui/Toast";
import AppTranslation from "../../../../service/app_translation/AppTranslation";
import { ReplyMessageDetailModel } from "../../../../model/chats/ReplyMessageDetailModel";
import { ForwardMessageList } from "../../../../model/chats/ForwardProfileListModel";

type TeamChatsContextType = {
  getSelectedChatSession: () => void;
  resetMessages: () => void;
  disconnectCallBacks: () => void;
  conversationCallBack: (eventName: string, ...args: any[]) => void;
  updateConversationSettings: (
    detail: UpdateConversationSettingsParams
  ) => void;
  updateMuteSettings: () => void;

  //socketStatus: boolean;
  userId: string;
  conversationMessages: Record<string, Array<TeamMessage>> | null;
  conversationDetail: TeamResults | null;
  participantDetail: ParticipantDetails | null;
  muteSettings: MuteSettingsType;

  onlineStatus: boolean;
  typingStatus: boolean;
  recordingStatus: boolean;

  // CHATTING
  sendTextMessage: (msg: string, callback: () => void) => void;
  sendAudioMessage: (
    file: File,
    secondsDuration: number,
    callback: () => void
  ) => void;
  sendMediaMessage: (
    file: File,
    durationSeconds: number,
    callback: () => void
  ) => void;

  // OPTION
  handleClickMessage: (message: TeamMessage) => void;
  handlePressStart: (message: TeamMessage) => void;
  handlePressEnd: () => void;
  clearLongPress: () => void;
  selectedMesasgeIds: Array<string>;
  selectedMessages: Array<TeamMessage>;
  longPressStatus: boolean;

  replyMessage: ReplyMessageDetailModel | null;
  longPressAction: (action: "reply" | "copy" | "delete" | "forward") => void;
  cancelReply: () => void;
};

const TeamChatsContext = createContext<TeamChatsContextType | undefined>(
  undefined
);

export const TeamChatsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const muteDurationTeamSelector = useSelector(
    (state: RootState) => state.chats.muteDurationTeam
  );

  const sendMessageDetailSelector = useSelector(
    (state: RootState) => state.chats.sendMessageDetail
  );

  const messageDeleteActionTypeSelector = useSelector(
    (state: RootState) => state.chats.messageDeleteActionType
  );

  const mediaDeleteActionTypeSelector = useSelector(
    (state: RootState) => state.chats.mediaDeleteActionType
  );

  const mediaDeleteEveryoneSelector = useSelector(
    (state: RootState) => state.chats.mediaDeleteEveryone
  );

  const forwardedMediaListSelector = useSelector(
    (state: RootState) => state.chats.forwardedMediaList
  );

  const forwardedMessagesSelector = useSelector(
    (state: RootState) => state.chats.forwardedMessages
  );

  const voiceLengthRef = useRef<string>("00:00");

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressStatusRef = useRef<boolean>(false);
  const selectedMesasgeIdsRef = useRef<Array<string>>([]);

  const userId = CacheStorage.getValue(StorageKeys.account_id) || "0";
  const privateKeyRef = useRef<string>(
    CacheStorage.getValue(StorageKeys.private_key) || ""
  );
  const publicKeyRef = useRef<string>(
    CacheStorage.getValue(StorageKeys.public_key) || ""
  );
  const chatSelectedRef = useRef<ConversationList | null>(null);
  const conversationDetailRef = useRef<TeamResults | null>(null);

  const conversationMessagesRef = useRef<Array<TeamMessage> | null>(null);
  const participantDetailRef = useRef<ParticipantDetails | null>(null);

  const muteSettingsRef = useRef<MuteSettingsType>(
    ChatsProvider.muteSettingsDefault
  );

  const [onlineStatus, setOnlineStatus] = useState<boolean>(false);
  const [typingStatus, setTypingStatus] = useState<boolean>(false);
  const [recordingStatus, setRecordingStatus] = useState<boolean>(false);

  const [conversationDetail, setConversationDetail] =
    useState<TeamResults | null>(null);
  const [participantDetail, setParticipantDetail] =
    useState<ParticipantDetails | null>(null);
  const [conversationMessages, setConversationMessages] = useState<Record<
    string,
    TeamMessage[]
  > | null>(null);

  const [muteSettings, setMuteSettings] = useState<MuteSettingsType>(
    ChatsProvider.muteSettingsDefault
  );

  const [longPressStatus, setLongPressStatus] = useState<boolean>(false);
  const [selectedMesasgeIds, setSelectedMesasgeIds] = useState<Array<string>>(
    []
  );
  const [selectedMessages, setSelectedMessages] = useState<Array<TeamMessage>>(
    []
  );

  const [replyMessage, setReplyMessage] =
    useState<ReplyMessageDetailModel | null>(null);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const resetValues = () => {
    privateKeyRef.current =
      CacheStorage.getValue(StorageKeys.private_key) || "";
    chatSelectedRef.current = null;

    setOnlineStatus(false);
    setTypingStatus(false);
    setRecordingStatus(false);

    muteSettingsRef.current = ChatsProvider.muteSettingsDefault;
    setMuteSettings(ChatsProvider.muteSettingsDefault);

    resetMessages();
    setReplyMessage(null);
  };

  const clearLongPress = () => {
    longPressStatusRef.current = false;
    setLongPressStatus(false);

    setSelectedMesasgeIds([]);
    selectedMesasgeIdsRef.current = [];
    setSelectedMessages([]);
  };

  const resetMessages = () => {
    conversationDetailRef.current =
      ChatsProvider.getChatConversationDetailSession();
    conversationMessagesRef.current = null;
    participantDetailRef.current = null;

    setConversationDetail(conversationDetailRef.current);
    setConversationMessages(conversationMessagesRef.current);
    setParticipantDetail(participantDetailRef.current);

    clearLongPress();
  };

  const cancelReply = () => {
    console.log(`cancelReply`);
    clearLongPress();
    setReplyMessage(null);
  };

  // GET SESSION SELECTED CHAT -> getting messages ////////////////////////////////////////////
  const getSelectedChatSession = async (
    getConversDetailStatus: boolean = true
  ) => {
    if (socket.connected) {
      const chatsChatSelectedSession = SessionStorage.getValue(
        StorageKeys.chats_chat_selected
      );

      if (UtilityHelper.isNotEmpty(chatsChatSelectedSession)) {
        const chatSelected: ConversationList = JSON.parse(
          chatsChatSelectedSession
        );

        chatSelectedRef.current = chatSelected;

        if (getConversDetailStatus) {
          SocketService.initConversation({
            flag: chatSelected?.flag!,
            eventN:
              chatSelected?.flag! === "3"
                ? SocketChatsKeys.join_chat_group
                : SocketChatsKeys.join_chat,
            convId: chatSelected?.conv_id!,
            // socketCallBack: conversationCallBack,
            callback: (response: any) => {
              if (response["status"] === "1") {
                const teamConversationDetailsModel: TeamConversationDetailsModel =
                  response;

                conversationDetailRef.current =
                  teamConversationDetailsModel?.results!;
                participantDetailRef.current =
                  teamConversationDetailsModel?.results?.participant_details!;

                setConversationDetail(conversationDetailRef.current);
                setParticipantDetail(participantDetailRef.current);

                ChatsProvider.setupChatConversationDetail({
                  flag: chatSelectedRef.current?.flag!,
                  detail: conversationDetailRef.current,
                });

                updateMuteSettings();
              }
            },
          });
        }
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateOnline = (acctId: string, status: boolean) => {
    if (
      participantDetailRef.current != null &&
      participantDetailRef.current?.account_id! === acctId
    ) {
      console.log(`online status : ${onlineStatus}`);
      setOnlineStatus(status);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateMuteSettings = () => {
    const mutedConversations: TeamMutedConversations =
      conversationDetailRef.current?.conv_settings?.muted_conversations!;
    muteSettingsRef.current = ChatsProvider.muteSettingsDefault;

    muteSettingsRef.current = {
      ...muteSettingsRef.current,
      muteStatus:
        conversationDetailRef.current?.conv_settings?.muted_conversations
          ?.mute_status!,
    };

    if (mutedConversations?.mute_status! === "1") {
      muteSettingsRef.current = {
        ...muteSettingsRef.current,
        muteDuration: mutedConversations?.mute_duration!,
        muteEnd:
          mutedConversations.mute_type! === "1"
            ? toLocalDate(
                new Date(mutedConversations?.mute_end!),
                "d MMMM, h:mm a",
                true
              )
            : "",
      };
    }

    setMuteSettings(muteSettingsRef.current);
  };

  const submitMuteDetail = (muteStatusVal: string) => {
    console.log(`submitMuteDetail: ${muteStatusVal}`);
    if (["1", "2", "3", "4", "5"].includes(muteStatusVal)) {
      const muteSettingsTemp: MuteSettingsType = ChatsProvider.updateMuteStatus(
        Number(muteStatusVal),
        muteSettingsRef.current
      );

      updateConversationSettings({
        convId: chatSelectedRef?.current?.conv_id!,
        eventName: SocketChatsKeys.chat_mute_notification,
        muteDuration: muteSettingsTemp?.muteDuration,
        muteStatus: muteSettingsTemp?.muteStatus,
        muteType: muteSettingsTemp?.muteType,
      });
    }
  };

  const updateBlockStatus = ({
    blockStatusVal,
    participantBlockStatusVal,
    participantBlockStatus,
    accountIdVal,
    profileId,
  }: UpdateBlockStatusParams) => {
    console.log(
      `updateBlockStatus: ${participantBlockStatus} ${userId} ${accountIdVal} ${profileId} ${participantDetailRef
        .current?.account_id!}`
    );
    let conversationDetailTemp: TeamResults = conversationDetailRef.current!;

    if (
      userId === accountIdVal &&
      profileId === participantDetailRef.current?.account_id!
    ) {
      if (participantBlockStatus) {
        if (
          conversationDetailTemp.conv_settings?.block_details
            ?.participant_block_status !== undefined
        ) {
          conversationDetailTemp = {
            ...conversationDetailTemp,
            conv_settings: {
              ...conversationDetailTemp.conv_settings,
              block_details: {
                ...conversationDetailTemp.conv_settings.block_details,
                participant_block_status: participantBlockStatusVal,
              },
            },
          };
        }
      } else {
        if (
          conversationDetailTemp?.conv_settings?.block_details
            ?.my_block_status !== undefined
        ) {
          conversationDetailTemp = {
            ...conversationDetailTemp,
            conv_settings: {
              ...conversationDetailTemp.conv_settings,
              block_details: {
                ...conversationDetailTemp.conv_settings.block_details,
                my_block_status: blockStatusVal,
              },
            },
          };
        }
      }
    }

    conversationDetailRef.current = conversationDetailTemp;
    setConversationDetail(conversationDetailTemp);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateDeleteStatusFromOther = (msgIds: string) => {
    const messagesIds: Array<string> = msgIds.split(",");
    let conversationMessagesTemp: TeamMessage[] = [
      ...conversationMessagesRef.current!,
    ];

    conversationMessagesTemp.map(async (element) => {
      if (messagesIds.includes(element?.message_id!)) {
        element.status = "0";
      }
    });

    conversationMessagesRef.current = conversationMessagesTemp;
    messageDecode(conversationMessagesTemp);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const readMessage = () => {
    let conversationMessagesTemp: TeamMessage[] = [
      ...conversationMessagesRef.current!,
    ];

    conversationMessagesTemp.map(async (element) => {
      if (element.read_status === "0") {
        element.read_status = "1";
      }
    });

    conversationMessagesRef.current = conversationMessagesTemp;
    messageDecode(conversationMessagesTemp);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateTypingStatus = (val: string, flag: string) => {
    let status: boolean = false;
    if (["1", "4"].includes(flag)) {
      status = val === "1";
      setTypingStatus(status);
      setRecordingStatus(false);
    } else {
      status = val === "1";
      setTypingStatus(false);
      setRecordingStatus(status);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const conversationCallBack = async (eventName: string, ...args: any[]) => {
    console.log(
      `ConversationCallBack Team: ${eventName} flag: ${chatSelectedRef.current
        ?.flag!}`,
      args
    );

    let hasConvId: boolean = false;
    let results: any = null;
    let resultConversationId: string = "";

    if (["1", "4"].includes(chatSelectedRef.current?.flag!)) {
      if (args.length > 0) {
        results = args[0]["results"] ?? null;
        hasConvId = results != null && "conv_id" in results;

        if (hasConvId) {
          resultConversationId = results["conv_id"];

          if (
            [
              SocketChatsKeys.profile_offline,
              SocketChatsKeys.profile_online,
            ].includes(eventName)
          ) {
            updateOnline(
              results["account_id"],
              eventName === SocketChatsKeys.profile_online
            );
          }

          if (UtilityHelper.isNotEmpty(resultConversationId)) {
            if (eventName === SocketChatsKeys.chat_message_history) {
              const teamConversationDetailsModel: TeamConversationMessageModel =
                args[0];

              conversationMessagesRef.current =
                teamConversationDetailsModel?.results?.messages!;

              messageDecode(conversationMessagesRef.current!);
              await UtilityHelper.delay();
              SocketService.readMessage(
                teamConversationDetailsModel?.results?.conv_id!,
                chatSelectedRef?.current?.flag!
              );

              //
            } else if (eventName === SocketChatsKeys.new_chat_message) {
              if (
                "message" in results &&
                "conv_id" in results &&
                results["conv_id"] === chatSelectedRef.current?.conv_id
              ) {
                const message: TeamMessage = results["message"];

                //conversationMessagesRef.current!.push(message);
                conversationMessagesRef.current = [
                  message,
                  ...conversationMessagesRef.current!,
                ];
                messageDecode(conversationMessagesRef.current!);

                if (userId !== message.account_id) {
                  await UtilityHelper.delay();
                  SocketService.readMessage(
                    resultConversationId,
                    chatSelectedRef?.current?.flag!
                  );
                }

                // RELOAD ONLY IN WEB LAYOUT
                if (type === 3) {
                  dispatch(chatRefreshListStatus(true));
                }
              }
              //
            } else if (eventName === SocketChatsKeys.profile_typing) {
              if ("typing_status" in results) {
                updateTypingStatus(
                  results["typing_status"],
                  results["typing_flag"]
                );
              }
              //
            } else if (eventName === SocketChatsKeys.mark_read_chat) {
              readMessage();
              //
            } else if (eventName === SocketChatsKeys.chat_block_profile) {
              if (
                "account_id" in results &&
                "profile_id" in results &&
                "participant_block_status" in results
              ) {
                updateBlockStatus({
                  participantBlockStatus: true,
                  accountIdVal: results["account_id"],
                  profileId: results["profile_id"],
                  participantBlockStatusVal:
                    results["participant_block_status"],
                });
              }
              //
            } else if (eventName === SocketChatsKeys.chat_delete_messages) {
              if ("message_id" in results) {
                updateDeleteStatusFromOther(results["message_id"]);
              }
            }
          }
        }
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const messageDecode = async (messages: Array<TeamMessage>) => {
    if (conversationDetailRef.current != null) {
      let messagesList: Array<TeamMessage> = [];

      messages!.map(async (element, index) => {
        if (element?.message! != null) {
          element.decripted_mesage = UtilityHelper.isStringMap(
            element?.message!
          )
            ? element?.message_encryption_type === "e2e"
              ? EncryptDecryptService.decryptWithPublicKey(
                  element.message![userId]!,
                  privateKeyRef.current
                )
              : ""
            : element?.message_encryption_type === "hybrid"
            ? EncryptDecryptService.hybridDecrypt(
                element.message![userId]!,
                privateKeyRef.current
              )
            : "";
        }

        if (element.replay_status === "1" && element.replay_message != null) {
          element.replay_message!.decripted_mesage = UtilityHelper.isStringMap(
            element.replay_message!.message
          )
            ? element?.replay_message?.message_encryption_type === "e2e"
              ? EncryptDecryptService.decryptWithPublicKey(
                  element.replay_message!.message![userId]!,
                  privateKeyRef.current
                )
              : ""
            : element?.replay_message?.message_encryption_type === "hybrid"
            ? EncryptDecryptService.hybridDecrypt(
                element.replay_message!.message![userId]!,
                privateKeyRef.current
              )
            : "";
        }

        if (element.decripted_mesage != null) {
          messagesList.push(element);
        }
      });

      messagesList = [...messagesList].sort(
        (a, b) => Number(b.message_id) - Number(a.message_id)
      );

      let messagesListGrouped: Record<string, TeamMessage[]> =
        TeamChatsProvider.groupByDate(messagesList);

      messagesListGrouped = Object.fromEntries(
        Object.entries(messagesListGrouped).reverse()
      );

      /*  const sortedEntries = Object.entries(messagesListGrouped).sort(
        ([dateA], [dateB]) =>
          new Date(dateA).getTime() - new Date(dateB).getTime()
      );

      messagesListGrouped = {};
      sortedEntries.map((item) => {
        messagesListGrouped[item[0]] = item[1];
      }); */

      setConversationMessages(messagesListGrouped);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateConversationSettings = async ({
    convId,
    eventName,
    exitFlag = "",
    muteStatus = "",
    muteDuration = "",
    muteType = "",
    profileId = "",
    blockStatus = "",
  }: UpdateConversationSettingsParams) => {
    console.log(`convId: ${convId}, eventName: ${eventName}`);

    if (
      UtilityHelper.isNotEmpty(convId) &&
      UtilityHelper.isNotEmpty(eventName)
    ) {
      if (eventName === SocketChatsKeys.chat_clear_messages) {
        SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          eventN: SocketChatsKeys.chat_clear_messages,
          callback: updateClearChatDate,
        });
      } else if (eventName === SocketChatsKeys.chat_block_profile) {
        SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          eventN: SocketChatsKeys.chat_block_profile,
          profileId: profileId,
          blockStatus: blockStatus,
          callback: (response: any) => {
            updateBlockStatus({
              participantBlockStatus: false,
              accountIdVal: response["results"]["account_id"],
              profileId: response["results"]["profile_id"],
              blockStatusVal: response["results"]["my_block_status"],
            });
          },
        });
      } else if (eventName === SocketChatsKeys.chat_mute_notification) {
        SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          eventN: SocketChatsKeys.chat_mute_notification,
          muteDuration: muteDuration,
          muteStatus: muteStatus,
          muteType: muteType,
          callback: async (response: any) => {
            conversationDetailRef.current = {
              ...conversationDetailRef.current,
              conv_settings: {
                ...conversationDetailRef.current?.conv_settings,
                muted_conversations: {
                  ...conversationDetailRef.current?.conv_settings
                    ?.muted_conversations!,
                  mute_status: response["results"]["mute_status"],
                  mute_type: response["results"]["mute_type"],
                  mute_start: response["results"]["mute_start"],
                  mute_duration: response["results"]["mute_duration"],
                  mute_end: response["results"]["mute_end"],
                },
              },
            };

            setConversationDetail(conversationDetailRef.current);
            updateMuteSettings();

            // RELOAD ONLY IN WEB LAYOUT
            if (type === 3) {
              dispatch(chatRefreshListStatus(true));
            }

            await UtilityHelper.delay(200);
            dispatch(removeModal(ModalKeys.chat_mute_notification));
          },
        });
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateClearChatDate = (response: any) => {
    const clearChatDateVal: string =
      response["results"]["clear_chat_date"] ?? "";

    if (UtilityHelper.isNotEmpty(clearChatDateVal)) {
      if (
        conversationDetailRef.current?.conv_settings?.clear_chat_date != null
      ) {
        conversationDetailRef.current = {
          ...conversationDetailRef.current,
          conv_settings: {
            ...conversationDetailRef.current?.conv_settings,
            clear_chat_date: clearChatDateVal,
          },
        };

        setConversationDetail(conversationDetailRef.current);

        conversationMessagesRef.current = [];
        setConversationMessages({});
      }
    }
  };

  /// SEND MESSAGE  //////////////////////////////////////////////////////////////////////////

  const sendTextMessage = (textMessage: string, callback: () => void) => {
    console.log(`sendTextMessage`);
    if (socket.connected && UtilityHelper.isNotEmpty(textMessage)) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      SocketService.typingChatMessage({
        convId: chatSelectedRef.current?.conv_id!,
        flag: "1",
        status: "0",
      });

      const txtType: ChatTextType = ChatsProvider.identifyTextType(textMessage);

      sendMessage({
        msg: textMessage,
        msgType: txtType === ChatTextType.url ? "9" : "1",
        callback: () => {
          callback();
        },
      });
    }
  };

  const sendLocation = (textMessage: string) => {
    sendMessage({
      msg: textMessage,
      msgType: "5",
      callback: () => {},
    });
  };

  const sendAudioMessage = async (
    file: File,
    durationSeconds: number,
    callback: () => void
  ) => {
    console.log(`sendAudioMessage`);
    if (socket.connected) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      voiceLengthRef.current = UtilityHelper.formatTimer(
        Math.ceil(durationSeconds)
      );

      SocketService.typingChatMessage({
        convId: chatSelectedRef.current?.conv_id!,
        flag: "2",
        status: "0",
      });

      sendMediaMessage(file, durationSeconds, callback);
    }
  };

  const sendMediaMessage = async (
    file: File,
    durationSeconds: number,
    callback: () => void
  ) => {
    console.log(`sendMediaMessage`);
    if (socket.connected) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      voiceLengthRef.current = UtilityHelper.formatTimer(
        Math.ceil(durationSeconds)
      );

      Loader.modal();
      const detailUploaded: UploadEncryptedMedia | null =
        await ImagePickerPreviewProvider.uploadMediaChatFile({
          convId: chatSelectedRef.current?.conv_id!,
          mediaFile: file,
          mediaFileName: file.name,
        });
      Loader.modal(false);

      if (detailUploaded != null) {
        const urlArr: string[] | undefined =
          detailUploaded.file_url?.split(".");
        const fileExt: string = urlArr![urlArr!.length - 1];

        let msg: string = "";

        const msgType: string = imageExtensions.includes(fileExt)
          ? "2"
          : audioExtensions.includes(fileExt)
          ? "3"
          : docExtensions.includes(fileExt)
          ? "6"
          : videoExtensions.includes(fileExt)
          ? "4"
          : pdfExtensions.includes(fileExt)
          ? "7"
          : "8";

        if (msgType === "3") {
          msg = `${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${voiceLengthRef.current}`;
        } else if (msgType === "4") {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${voiceLengthRef.current}`;
        } else if (["6", "7", "8"].includes(msgType)) {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${detailUploaded.original_name}`;
        } else {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}`;
        }

        sendMessage({
          msg: msg,
          msgType: msgType,
          fileId: detailUploaded.file_id,
          callback: () => {
            callback();
          },
        });
      }
    }
  };

  const sendMessage = async ({
    msg,
    msgType,
    fileId = "",
    replyId = "",
    forwardStatus = "0",
    forwardCovId = "",
    forwardAccId = "",
    forwardKey = "",
    forwardChatFlag = "",
    callback,
  }: {
    msg: string;
    msgType: string;
    fileId?: string;
    replyId?: string;
    forwardStatus?: string;
    forwardCovId?: string;
    forwardAccId?: string;
    forwardKey?: string;
    forwardChatFlag?: string;
    callback?: (res?: any) => void;
  }) => {
    if (
      UtilityHelper.isNotEmpty(msg) &&
      userId !== "0" &&
      UtilityHelper.isNotEmpty(privateKeyRef.current)
    ) {
      if (replyMessage != null) {
        replyId = replyMessage?.message_id!;
      }

      console.log(forwardChatFlag);
      console.log(forwardStatus);
      if (
        UtilityHelper.isNotEmpty(forwardChatFlag) &&
        ["2"].includes(forwardChatFlag) &&
        forwardStatus === "1"
      ) {
        const encVal: GroupMessageEncryptedType | null =
          await EncryptDecryptService.encryptGroup(forwardKey, msg);

        if (encVal != null) {
          SocketService.sendDeptMessage({
            flag: forwardChatFlag,
            convId: forwardCovId,
            message: encVal.cipherText,
            messageIv: encVal.iv,
            messageTag: encVal.tag,
            messageType: msgType,
            forwardStatus: forwardStatus,
            fileId: fileId,
            replyId: replyId,
            callback: () => {},
          });
        }
      } else {
        if (msg.length < ChatsProvider.messageMaxLength) {
          const senEncMsg: string | null =
            EncryptDecryptService.encryptWithPublicKey(
              msg,
              publicKeyRef.current
            );

          const recEncMsg: string | null =
            EncryptDecryptService.encryptWithPublicKey(
              msg,
              forwardStatus === "1"
                ? forwardKey
                : participantDetail!.public_key!
            );

          if (senEncMsg != null && recEncMsg != null) {
            const dataMessage: Record<string, string> = {
              [userId]: senEncMsg,
              [forwardStatus === "1"
                ? forwardAccId
                : participantDetail!.account_id!]: recEncMsg,
            };

            console.log(dataMessage);

            SocketService.sendTeamMessage({
              flag: chatSelectedRef.current?.flag!,
              convId:
                forwardStatus === "1"
                  ? forwardCovId
                  : chatSelectedRef.current?.conv_id!,
              message: dataMessage,
              messageType: msgType,
              forwardStatus: forwardStatus,
              fileId: fileId,
              replyId: replyId,
              callback: () => {
                if (callback !== undefined) {
                  callback();
                }

                sendAutoReplyMessage();
                clearLongPress();
                cancelReply();
              },
            });
          }
        } else {
          const senEncMsg: HybridEncrypted | null =
            EncryptDecryptService.hybridEncrypt(msg, publicKeyRef.current);

          const recEncMsg: HybridEncrypted | null =
            EncryptDecryptService.hybridEncrypt(
              msg,
              forwardStatus === "1"
                ? forwardKey
                : participantDetail!.public_key!
            );

          if (senEncMsg != null && recEncMsg != null) {
            const dataMessage: Record<string, HybridEncrypted> = {
              [userId]: senEncMsg,
              [forwardStatus === "1"
                ? forwardAccId
                : participantDetail!.account_id!]: recEncMsg,
            };

            SocketService.sendTeamMessage({
              flag: chatSelectedRef.current?.flag!,
              convId:
                forwardStatus === "1"
                  ? forwardCovId
                  : chatSelectedRef.current?.conv_id!,
              message: {},
              hybridMessage: dataMessage,
              messageType: msgType,
              forwardStatus: forwardStatus,
              fileId: fileId,
              replyId: replyId,
              callback: () => {
                sendAutoReplyMessage();
                clearLongPress();

                if (callback !== undefined) {
                  callback();
                }
              },
            });
          }
        }
      }
    }
  };

  const sendAutoReplyMessage = () => {
    if (
      conversationDetailRef.current?.conv_settings?.receiver_auto_reply_details
        ?.status === "1"
    ) {
      const msg: string =
        conversationDetailRef.current?.conv_settings
          ?.receiver_auto_reply_details?.message!;

      const senEncMsg: HybridEncrypted | null =
        EncryptDecryptService.hybridEncrypt(msg, publicKeyRef.current);

      const recEncMsg: HybridEncrypted | null =
        EncryptDecryptService.hybridEncrypt(
          msg,
          participantDetail!.public_key!
        );

      if (senEncMsg != null && recEncMsg != null) {
        const dataMessage: Record<string, HybridEncrypted> = {
          [userId]: senEncMsg,
          [conversationDetailRef.current?.conv_settings
            ?.receiver_auto_reply_details?.account_id!]: recEncMsg,
        };

        SocketService.sendTeamMessage({
          flag: chatSelectedRef.current?.flag!,
          convId: chatSelectedRef.current?.conv_id!,
          message: {},
          hybridMessage: dataMessage,
          messageType: "13",
          callback: () => {},
        });
      }
    }
  };

  const deleteMessages = (flagId: "0" | "1") => {
    if (selectedMesasgeIdsRef.current.length > 0) {
      updateDeleteStatus(flagId);
    }
  };

  const deleteMediaMessages = (flagId: "0" | "1") => {
    if (mediaDeleteEveryoneSelector != null) {
      if ((mediaDeleteEveryoneSelector?.messagesId ?? []).length > 0) {
        selectedMesasgeIdsRef.current = mediaDeleteEveryoneSelector?.messagesId;

        updateDeleteStatus(flagId);

        // REMOVE MODAL
        if (type !== 3) {
          dispatch(removeModal(ModalKeys.chat_media));
          window.history.replaceState(null, "", window.location.pathname);
        }
      }
    }
  };

  const updateDeleteStatus = (flagId: "0" | "1") => {
    if (selectedMesasgeIdsRef.current.length > 0) {
      SocketService.deleteMessage({
        flag: chatSelectedRef.current?.flag!,
        convId: chatSelectedRef.current?.conv_id!,
        msgIDs: selectedMesasgeIdsRef.current.join(","),
        status: flagId,
        callback: (response: any) => {
          updateDeleteStatusFromOther(selectedMesasgeIdsRef.current.join(","));
          clearLongPress();
        },
      });
    }
  };

  const sendForwardMessageList = async (
    profileConvers: Array<ForwardMessageList>
  ) => {
    console.log("sendForwardMessageList");
    if (forwardedMediaListSelector != null && profileConvers.length > 0) {
      Loader.modal();
      await Promise.all(
        profileConvers.map(async (conv) => {
          forwardedMediaListSelector.map(async (msg) => {
            await sendMessage({
              msg: msg.decripted_mesage!,
              msgType: msg.message_type!,
              fileId: msg.file_id!,
              forwardStatus: "1",
              forwardCovId: conv.conv_id!,
              forwardAccId: conv.account_id!,
              forwardKey: conv.key!,
              forwardChatFlag: conv.flag,
              callback: () => {},
            });
          });
        })
      );
      Loader.modal(false);
    } else {
      if (
        selectedMesasgeIdsRef.current.length > 0 &&
        profileConvers.length > 0
      ) {
        Loader.modal();
        await Promise.all(
          profileConvers.map(async (conv) => {
            selectedMessages.map(async (msg) => {
              /* if (["1", "5", "9"].includes(msg.message_type!)) {
                await sendMessage({
                  msg: msg.decripted_mesage!,
                  msgType: msg.message_type!,
                  fileId: msg.file_id!,
                  forwardStatus: "1",
                  forwardCovId: conv.conv_id!,
                  forwardAccId: conv.account_id!,
                  forwardKey: conv.key!,
                  forwardChatFlag: conv.flag,
                  callback: () => {},
                });
              } else { */
              await sendMessage({
                msg: msg.decripted_mesage!,
                msgType: msg.message_type!,
                fileId: msg.file_id!,
                forwardStatus: "1",
                forwardCovId: conv.conv_id!,
                forwardAccId: conv.account_id!,
                forwardKey: conv.key!,
                forwardChatFlag: conv.flag,
                callback: () => {},
              });
              //}
            });
          })
        );
        Loader.modal(false);
      }
    }

    console.log("clear forward");

    UtilityHelper.delay(100);
    dispatch(removeModal(ModalKeys.chat_forward));
    dispatch(removeModal(ModalKeys.chat_forward_media));
    clearLongPress();
    cancelReply();

    // CLEAR MEDIA SELECTION
    dispatch(mediaDeleteChooseAction(null));
    dispatch(setForwardMediaList(null));

    // RELOAD ONLY IN WEB LAYOUT
    if (type === 3) {
      dispatch(chatRefreshListStatus(true));
    } else {
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const handlePressStart = (msdDetail: TeamMessage) => {
    timerRef.current = setTimeout(() => {
      longPressStatusRef.current = true;
      setLongPressStatus(true);
      console.log("Long press detected!");

      if (isMobile) {
        toggleMessageIdSelection(msdDetail);
      }
    }, 500);
  };

  const handlePressEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    console.log("Press and hold");
  };

  const handleClickMessage = (msdDetail: TeamMessage) => {
    console.log(`handleClickMessage: ${longPressStatusRef.current}`);
    if (longPressStatusRef.current) {
      toggleMessageIdSelection(msdDetail);

      if (selectedMesasgeIdsRef.current.length === 0) {
        clearLongPress();
      }
    }
  };

  const toggleMessageIdSelection = (msdDetail: TeamMessage) => {
    if (msdDetail.status === "1" && !msdDetail.deleted?.includes(userId)) {
      let ids = [...selectedMesasgeIdsRef.current];
      const id = msdDetail?.message_id!;

      if (ids.includes(id)) {
        ids = ids.filter((item) => item !== id);
      } else {
        ids.push(id);
      }

      selectedMesasgeIdsRef.current = ids;
      setSelectedMesasgeIds(ids);

      const messsageList: Array<TeamMessage> =
        conversationMessagesRef.current!.filter((msg) =>
          ids.includes(msg.message_id!)
        );
      setSelectedMessages(messsageList);
    }
  };

  const longPressAction = async (
    action: "reply" | "copy" | "delete" | "forward"
  ) => {
    if (selectedMesasgeIdsRef.current.length > 0) {
      console.log(`longPressAction: ${action}`);
      const messageSelected: TeamMessage = selectedMessages[0];
      const chatDecryptedMessage: string =
        messageSelected.decripted_mesage ?? "";

      if (action === "reply") {
        setReplyMessage({
          message_id: messageSelected.message_id,
          account_id: messageSelected.account_id,
          name: participantDetailRef.current?.name!,
          message_type: messageSelected.message_type,
          decripted_mesage: chatDecryptedMessage,
        });
        clearLongPress();
      } else if (action === "copy") {
        let copyMsg: string = "";
        switch (messageSelected.message_type) {
          case "1":
          case "9":
            copyMsg = chatDecryptedMessage;
            break;
          case "2":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
          case "3":
            copyMsg = chatDecryptedMessage.split("|")[0];
            break;
          case "4":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
          case "5":
            copyMsg = `https://maps.google.com/?q=${
              chatDecryptedMessage.split("|")[1]
            },${chatDecryptedMessage.split("|")[2]}`;
            break;
          case "6":
          case "7":
          case "8":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
        }

        clearLongPress();

        try {
          await navigator.clipboard.writeText(copyMsg);
          ShowToast.success(AppTranslation.text("text_copied_to_clipboard"));
        } catch (err) {
          ShowToast.error("Failed to copy!");
          console.error("Failed to copy!", err);
        }
      } else if (action === "delete") {
        dispatch(
          addModal({
            id: ModalKeys.chat_delete_message,
            title:
              selectedMesasgeIdsRef.current.length > 1
                ? "delete_messages"
                : "delete_message",
            titleCenter: true,
            allowBottomSheet: true,
            size: "md",
          })
        );
      } else if (action === "forward") {
        //clearLongPress(); // keep the mesages
        dispatch(
          addModal({
            id: ModalKeys.chat_forward,
            title: "forward",
            size: "lg",
            screenMobileView: type === 3 ? false : true,
          })
        );
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const disconnectCallBacks = async () => {
    if (socket.connected) {
      SocketService.disconnectCallBacks(conversationCallBack);
      console.log(`socket disconnect - TeamChatsContextProvider`);
      //SocketService.disconnect();
      //await UtilityHelper.delay();
      SocketService.leaveChat();
    }
  };

  useEffect(() => {
    if (
      muteDurationTeamSelector != null &&
      UtilityHelper.isNotEmpty(muteDurationTeamSelector)
    ) {
      //updateMuteStatusVal(muteDurationTeamSelector);
      submitMuteDetail(muteDurationTeamSelector);
    }
    // eslint-disable-next-line
  }, [muteDurationTeamSelector]);

  // MY MESSAGES
  useEffect(() => {
    if (sendMessageDetailSelector != null) {
      getSelectedChatSession(false);

      if (["1", "4"].includes(chatSelectedRef.current?.flag!)) {
        if (sendMessageDetailSelector.messageType === "1") {
          sendTextMessage(sendMessageDetailSelector.message, () => {});
        } else if (sendMessageDetailSelector.messageType === "5") {
          sendLocation(sendMessageDetailSelector.message);
        }
      }
      dispatch(sendChatMessage(null));
    }
    // eslint-disable-next-line
  }, [sendMessageDetailSelector]);

  // DELETE MESSAGES
  useEffect(() => {
    if (messageDeleteActionTypeSelector != null) {
      getSelectedChatSession(false);

      if (["1", "4"].includes(chatSelectedRef.current?.flag!)) {
        deleteMessages(messageDeleteActionTypeSelector);
      }
      dispatch(messageDeleteChooseAction(null));
    }
    // eslint-disable-next-line
  }, [messageDeleteActionTypeSelector]);

  // DELETE MEDIA
  useEffect(() => {
    if (mediaDeleteActionTypeSelector != null) {
      getSelectedChatSession(false);

      if (["1", "4"].includes(chatSelectedRef.current?.flag!)) {
        deleteMediaMessages(mediaDeleteActionTypeSelector);
      }
      dispatch(mediaDeleteChooseAction(null));
    }
    // eslint-disable-next-line
  }, [mediaDeleteActionTypeSelector]);

  // FORWARD MESSAGES
  useEffect(() => {
    if (forwardedMessagesSelector != null) {
      getSelectedChatSession(false);

      if (["1", "4"].includes(chatSelectedRef.current?.flag!)) {
        sendForwardMessageList(forwardedMessagesSelector);
      }
      dispatch(sendForwardMessages(null));
    }
    // eslint-disable-next-line
  }, [forwardedMessagesSelector]);

  useEffect(() => {
    resetValues();
    // eslint-disable-next-line
  }, []);

  return (
    <TeamChatsContext.Provider
      value={{
        getSelectedChatSession,
        resetMessages,
        conversationCallBack,
        disconnectCallBacks,
        updateConversationSettings,
        updateMuteSettings,

        userId,
        conversationMessages,
        conversationDetail,
        participantDetail,
        muteSettings,

        onlineStatus,
        typingStatus,
        recordingStatus,

        // CHATTING
        sendTextMessage,
        sendAudioMessage,
        sendMediaMessage,

        // OPTION
        handleClickMessage,
        handlePressStart,
        handlePressEnd,
        clearLongPress,
        selectedMesasgeIds,
        selectedMessages,
        longPressStatus,

        replyMessage,
        longPressAction,
        cancelReply,
      }}
    >
      {children}
    </TeamChatsContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const TeamChatsContextImport = () => {
  const context = useContext(TeamChatsContext);
  if (!context) {
    throw new Error(
      "TeamChatsContextImport must be used within a TeamChatsContextProvider"
    );
  }
  return context;
};
